// @ts-nocheck

import axios from "axios";
import {
  BACKEND_API_BASE_URL,
  AI_AGENT_API_PATH,
  CLIENT_SOURCE_HEADER,
  CLIENT_SOURCE,
} from "../utilities/constant";
import { getUserAccessToken } from "../utilities/setting";


const agentClient = axios.create({
  baseURL: `${BACKEND_API_BASE_URL}${AI_AGENT_API_PATH}`,
  timeout: 60000,
});

function authHeaders(extra = {}) {
  return {
    Authorization: getUserAccessToken(),
    [CLIENT_SOURCE_HEADER]: CLIENT_SOURCE,
    ...extra,
  };
}

function extractErrorMessage(error) {
  if (!error) return "Something went wrong. Please try again.";
  if (error.response === undefined) {
    return `Cannot reach the server. Confirm your connection and try again.`;
  }

  const detail = error.response?.data?.error?.explanation || error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.type)
      .filter(Boolean)
      .join(". ") || "Validation failed.";
  }
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (typeof error.response?.data?.message === "string") {
    return error.response.data.message;
  }
  return `Request failed (${error.response?.status || "unknown"}).`;
}

//  POST /v2/ai-agents/flow?format=json|xml|drawio
export const createFlow = async (payload, format = "json") => {
  try {
    const response = await agentClient.post("/flow", payload, {
      headers: authHeaders(),
      params: { format },
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// POST /v2/ai-agents/save

export const saveBot = async (payload) => {
  try {
    const response = await agentClient.post("/save", payload, {
      headers: authHeaders(),
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// POST /v2/ai-agents/draft 

export const saveDraft = async (payload) => {
  try {
    const response = await agentClient.post("/draft", payload, {
      headers: authHeaders(),
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// GET /v2/ai-agents/draft/{id}

export const getDraft = async (id) => {
  try {
    const response = await agentClient.get(`/draft/${encodeURIComponent(id)}`, {
      headers: authHeaders(),
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// DELETE /v2/ai-agents/draft/{id}

export const deleteDraft = async (id) => {
  try {
    const response = await agentClient.delete(`/draft/${encodeURIComponent(id)}`, {
      headers: authHeaders(),
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// GET /v2/ai-agents/name-availability?name=...  

export const checkAgentNameAvailability = async (name, agentId) => {
  try {
    const response = await agentClient.get("/name-availability", {
      headers: authHeaders(),
      params: { name, agent_id: agentId || undefined },
    });
    const data = response.data?.data ?? response.data;
    return { available: Boolean(data?.available) };
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// GET /v2/ai-agents/licence — remaining AI Agent create licences

export const getAiAgentLicence = async () => {
  try {
    const response = await agentClient.get("/licence", { headers: authHeaders() });
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

//  POST /v2/ai-agents/list

export const listAgents = async (userId) => {
  try {
    const response = await agentClient.post(
      "/list",
      { user_id: userId },
      { headers: authHeaders() },
    );
    const data = response.data?.data ?? response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.agents)) return data.agents;
    return [];
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// POST /v2/ai-agents/{agentId}

export const getAgent = async (agentId, userId) => {
  try {
    const response = await agentClient.post(
      `/${encodeURIComponent(agentId)}`,
      { user_id: String(userId ?? '') },
      { headers: authHeaders() },
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// DELETE /v2/ai-agents/{agentId}  (soft delete)

export const deleteAgent = async (agentId, userId) => {
  try {
    const response = await agentClient.delete(`/${encodeURIComponent(agentId)}`, {
      headers: authHeaders(),
      data: { user_id: String(userId ?? '') },
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// POST /v2/ai-agents/{agentId}/restore  — brings a soft-deleted agent back

export const restoreAgent = async (agentId, userId) => {
  try {
    const response = await agentClient.post(
      `/${encodeURIComponent(agentId)}/restore`,
      { user_id: String(userId ?? '') },
      { headers: authHeaders() },
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// POST /v2/ai-agents/{agentId}/completion-types

export const getCompletionTypes = async (agentId) => {
  try {
    const response = await agentClient.post(
      `/${encodeURIComponent(agentId)}/completion-types`,
      {},
      { headers: authHeaders() },
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// GET /v2/ai-agents/voices  — \ Voice ID dropdown: [{ id, name }]

export const getVoices = async () => {
  try {
    const response = await agentClient.get("/voices", { headers: authHeaders() });
    const data = response.data?.data ?? response.data;
    return Array.isArray(data) ? data : data?.voices || [];
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// GET /v2/ai-agents/persona-types  —  Persona Type dropdown: [{ id, type, name }]

export const getPersonaTypes = async () => {
  try {
    const response = await agentClient.get("/persona-types", { headers: authHeaders() });
    const data = response.data?.data ?? response.data;
    return Array.isArray(data) ? data : data?.persona_types || [];
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

// GET /v2/ai-agents/languages  — \Language dropdown: [{ id, language, code }]\

export const getLanguages = async () => {
  try {
    const response = await agentClient.get("/languages", { headers: authHeaders() });
    const data = response.data?.data ?? response.data;
    return Array.isArray(data) ? data : data?.languages || [];
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};